import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { useAlmacenes } from "../../hooks/useAlmacenes";
import { BOTON_SECUNDARIO_SX } from "../../../../components/ui/buttonStyles";

/**
 * Confirmación para borrar el conteo ya guardado de un artículo en una nave
 * (contada por error). Se borra en el servidor al confirmar, no al pulsar Guardar
 */
export default function ModalQuitarNave({ show, onHide, onConfirm, conteo, loading = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { getNombreAlmacen } = useAlmacenes();
    const nombreNave = conteo ? getNombreAlmacen(conteo.almacen) : "";

    return (
        <Dialog
            open={show}
            onClose={loading ? undefined : onHide}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", m: isMobile ? 0 : 2 } }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <DeleteIcon sx={{ color: "#cc071e", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: "#212529", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                        {isMobile ? "Quitar nave" : "Quitar conteo de la nave"}
                    </Typography>
                </Box>
                <IconButton onClick={onHide} disabled={loading} size="small" sx={{ color: "#6c757d" }}>
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, px: { xs: 2, sm: 3 }, flex: isMobile ? 1 : "initial" }}>
                {conteo && (
                    <Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: "#495057", lineHeight: 1.6 }}>
                        Se borrará lo contado de <strong>{conteo.referencia}</strong> en <strong>{nombreNave}</strong> (
                        {conteo.cantidadContada}). Las demás naves de este artículo no cambian.
                    </Typography>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: "column-reverse", sm: "row" } }}>
                <Button
                    variant="outlined"
                    onClick={onHide}
                    fullWidth={isMobile}
                    disabled={loading}
                    sx={{ ...BOTON_SECUNDARIO_SX, height: "auto", px: { xs: 2, sm: 2.5 }, py: { xs: 1.25, sm: 1 }, fontSize: { xs: 14, sm: 15 } }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    fullWidth={isMobile}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        backgroundColor: "#cc071e",
                        "&:hover": { backgroundColor: "#a30618" },
                        "&:disabled": { backgroundColor: "#6c757d", color: "#fff" },
                    }}
                >
                    {loading ? "Quitando..." : isMobile ? "Quitar" : "Quitar conteo"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
